import axios, { AxiosInstance, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import config from "../config/env";
import { tokenRefreshService } from "./tokenRefreshService";

export interface LoginRequest {
  companyName: string;
  userId: string;
  userPassword: string;
}

export interface LoginResponse {
  success: boolean;
  /** エラー種別。フロントエンドの表示切り替えに使用 */
  errorCode?: 'AUTH_ERROR' | 'LICENSE_ERROR' | 'SYSTEM_ERROR';
  message?: string;
  user?: {
    userid: string;
    email: string;
    name: string;
    companyName?: string;
  };
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  message?: string;
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
}

export interface GetMeResponse {
  success: boolean;
  data?: {
    appCd: number;
    isHasCollaboAPIKey: boolean;
    isHasCollaboUserid: boolean;
  };
  message?: string;
  error?: string;
}

class AuthService {
  private client: AxiosInstance;

  constructor() {
    const baseURL = config.apiUrl;

    this.client = axios.create({
      baseURL: `${baseURL}/auth`,
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor for debugging
    this.client.interceptors.request.use(
      config => {
        return config;
      },
      error => {
        console.error("Auth request error:", error);
        return Promise.reject(error);
      }
    );

    // Setup automatic token refresh FIRST (before other interceptors)
    tokenRefreshService.setupAxiosInterceptor(this.client);

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => {
        // Check for API errors in successful HTTP responses (200 status with error in body)
        if (response.data && response.data.success === false) {
          // DEPRECATED: Temporarily disabled error navigation
          // navigationService.handleApiFailure();

          // Transform the response to error format
          const apiError = response.data;
          return Promise.reject({
            success: false,
            message:
              apiError.error ||
              apiError.message ||
              "サーバーエラーが発生しました",
            errors: apiError.errors || [
              {
                field: "general",
                message: apiError.message || "サーバーエラーが発生しました",
              },
            ],
          });
        }

        return response;
      },
      error => {
        console.error(
          "Auth response error:",
          error.response?.data || error.message
        );

        // Handle timeout errors
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          toast.error(
            "リクエストがタイムアウトしました。しばらく待ってから再試行してください。"
          );
          return Promise.reject({
            success: false,
            message:
              "リクエストがタイムアウトしました。しばらく待ってから再試行してください。",
            errors: [
              {
                field: "general",
                message:
                  "ネットワーク接続が遅いか、サーバーが応答していません。",
              },
            ],
            isTimeout: true,
          });
        }

        // Don't handle 401 errors - let token refresh interceptor handle them
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }

        // Handle API failures with navigation callback (except 401 which is handled by token refresh)
        if (error.response?.status >= 400) {
          // DEPRECATED: Temporarily disabled error navigation
          // navigationService.handleApiFailure();
        }

        // Transform error response to ensure consistent structure
        if (error.response?.data) {
          const apiError = error.response.data;
          console.log("API Error Response:", apiError);
          return Promise.reject({
            success: false,
            message: apiError.error || "サーバーエラーが発生しました",
            errors: apiError.errors || [
              {
                field: "general",
                message: apiError.message || "サーバーエラーが発生しました",
              },
            ],
            status: error.response?.status, // Preserve status code
            response: error.response, // Preserve response for status checking
          });
        }

        return Promise.reject({
          success: false,
          message: "ネットワークエラーが発生しました",
          errors: [
            {
              field: "general",
              message:
                "サーバーに接続できません。ネットワークを確認してください。",
            },
          ],
        });
      }
    );
  }

  /**
   * Login user with credentials
   */
  async login(loginData: LoginRequest): Promise<LoginResponse> {
    try {
      // Validate required fields
      if (
        !loginData.companyName?.trim() ||
        !loginData.userId?.trim() ||
        !loginData.userPassword?.trim()
      ) {
        throw {
          success: false,
          message: "すべてのフィールドを入力してください",
          errors: [
            {
              field: "companyName",
              message: !loginData.companyName?.trim()
                ? "インスタンス名を入力してください"
                : "",
            },
            {
              field: "userId",
              message: !loginData.userId?.trim()
                ? "ユーザーIDを入力してください"
                : "",
            },
            {
              field: "userPassword",
              message: !loginData.userPassword?.trim()
                ? "パスワードを入力してください"
                : "",
            },
          ].filter(err => err.message),
        };
      }

      // Always use real API - remove demo mode for production
      const response: AxiosResponse<LoginResponse> = await this.client.post(
        "/login",
        {
          companyName: loginData.companyName,
          userId: loginData.userId,
          userPassword: loginData.userPassword,
        }
      );

      // Store tokens in cookies if provided
      if (response.data.success) {
        const { accessToken, refreshToken, user, expiresIn } = response.data;

        if (accessToken) {
          // Calculate expiration time based on expiresIn (in seconds) or default to 1 hour
          const expirationHours = expiresIn ? expiresIn / 3600 : 1 / 24; // Convert seconds to hours

          // Set access token cookie
          Cookies.set("access_token", accessToken, {
            expires: expirationHours,
            secure: true, // HTTPS only in production
            sameSite: "strict", // CSRF protection
          });
        }

        if (refreshToken) {
          const refreshTokenExpires = (() => {
            try {
              const payload = JSON.parse(atob(refreshToken.split(".")[1]));
              if (payload.exp && payload.iat) {
                return (payload.exp - payload.iat) / (60 * 60 * 24); // days
              }
            } catch (e) {}
            return 7;
          })();
          Cookies.set("refresh_token", refreshToken, {
            expires: refreshTokenExpires,
            secure: true, // HTTPS only in production
            sameSite: "strict", // CSRF protection
          });
        }

        if (user) {
          // Store user data in localStorage (less sensitive)
          localStorage.setItem("user_data", JSON.stringify(user));
        }
      }

      return response.data;
    } catch (error: any) {
      console.error("Login API error:", error);

      // LICENSE_ERROR: トーストではなく呼び元に errorCode を伝播しモーダルで表示させる
      const errorCode: string | undefined = error.response?.data?.errorCode;
      if (errorCode === 'LICENSE_ERROR') {
        return {
          success: false,
          errorCode: 'LICENSE_ERROR',
          message: error.response?.data?.error || 'ライセンスエラーが発生しました',
        };
      }

      // その他のエラー: トーストで表示
      const errorMessage =
        error.response?.data?.error || 'ログインに失敗しました';
      const errors = error.errors || [
        { field: 'general', message: errorMessage },
      ];

      // Show error toast with message from API
      toast.error(errorMessage);

      return {
        success: false,
        message: errorMessage,
        errors: errors,
      };
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      // Clear cookies and localStorage immediately
      Cookies.remove("access_token");
      Cookies.remove("refresh_token");
      localStorage.removeItem("user_data");
      console.log("All tokens and user data cleared");

      // Show success message
      toast.success("ログアウトしました");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("ログアウト中にエラーが発生しました");
    }
  }

  /**
   * Get current user data from localStorage
   */
  getCurrentUser(): any | null {
    try {
      const userData = localStorage.getItem("user_data");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const accessToken = Cookies.get("access_token");
    return !!accessToken;
  }

  /**
   * Get access token from cookie
   */
  getAccessToken(): string | null {
    return Cookies.get("access_token") || null;
  }

  /**
   * Get refresh token from cookie
   */
  getRefreshToken(): string | null {
    return Cookies.get("refresh_token") || null;
  }

  /**
   * Get auth token (backward compatibility)
   */
  getToken(): string | null {
    return this.getAccessToken();
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        console.log("No refresh token available");
        return false;
      }

      const response: AxiosResponse<LoginResponse> = await this.client.post(
        "/refresh",
        {
          refreshToken: refreshToken,
        }
      );

      if (response.data.success && response.data.accessToken) {
        // Calculate expiration time based on expiresIn (in seconds) or default to 1 hour
        const expirationHours = response.data.expiresIn
          ? response.data.expiresIn / 3600
          : 1 / 24;

        // Update access token in cookie
        Cookies.set("access_token", response.data.accessToken, {
          expires: expirationHours,
          secure: true,
          sameSite: "strict",
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error("Token refresh error:", error);
      toast.error("トークンの更新に失敗しました");
      return false;
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return {
          success: false,
          message: "Refresh token not found",
        };
      }

      const response: AxiosResponse<RefreshTokenResponse> =
        await this.client.post("/refresh", {
          refreshToken: refreshToken,
        });

      if (response.data.success && response.data.accessToken) {
        // Update tokens in cookies
        const expirationHours = response.data.expiresIn
          ? response.data.expiresIn / 3600
          : 24;
        Cookies.set("access_token", response.data.accessToken, {
          expires: expirationHours,
          secure: true,
          sameSite: "strict",
        });

        if (response.data.refreshToken) {
          Cookies.set("refresh_token", response.data.refreshToken, {
            expires: 7, // 7 days
            secure: true,
            sameSite: "strict",
          });
        }
      }

      return response.data;
    } catch (error: any) {
      console.error("Token refresh failed:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Token refresh failed",
      };
    }
  }

  async getMe(): Promise<GetMeResponse> {
    try {
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        return {
          success: false,
          message: "Access token not found",
          error: "Access token not found",
        };
      }

      const response: AxiosResponse<GetMeResponse> = await this.client.get(
        "/me",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Get me failed:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Get me failed",
        error: error.response?.data?.error || "Failed to get user information",
      };
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
